import React from "react";
import { Settings, BarChart3, Filter } from "lucide-react";
import SearchBar from "../../../components/SearchBar";
import ReusableDropdown from "../../../components/ReusableDropdown";
import { text } from "../constants/text";

export default function FiltersPanel({
  theme,
  language,
  styles,
  searchTerm,
  setSearchTerm,
  filterDepartment,
  setFilterDepartment,
  filterStatus,
  setFilterStatus,
  filterCompany,
  setFilterCompany,
  departmentsList = [],
  companiesList = [],
  screenSize,
  filteredCount,
  totalCount,
  actionButtons,
}) {
  const isRTL = language === "ar";

  // ── Shared label style (same as Attendance) ──
  const labelStyle = {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginBottom: "8px",
    fontSize: "14px",
    fontWeight: "600",
    flexDirection: "row",
    textAlign: isRTL ? "right" : "left",
  };

  return (
    <div>
      {/* ── Header row (same as Attendance FiltersPanel) ───────────── */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
          flexWrap: "wrap",
          gap: "16px",
          flexDirection: isRTL ? "row" : "row",
        }}
      >
        <div>
          <h3
            style={{
              fontSize: "clamp(16px, 3vw, 20px)",
              fontWeight: "700",
              margin: 0,
              display: "flex",
              alignItems: "center",
              gap: "10px",
              flexDirection: isRTL ? "row" : "row",
            }}
          >
            <Settings size={20} />
            {text[language]?.filtersActions || "Filters & Actions"}
          </h3>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "14px",
            flexWrap: "wrap",
            flexDirection: isRTL ? "row-reverse" : "row",
          }}
        >
          <div
            style={{
              fontSize: "14px",
              color: theme === "dark" ? "rgba(255,255,255,0.7)" : "#6b7280",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              flexDirection: isRTL ? "row" : "row",
            }}
          >
            <BarChart3 size={16} />
            {text[language]?.showing || "Showing"}{" "}
            {typeof filteredCount !== "undefined" ? filteredCount : ""}{" "}
            {text[language]?.of || "of"}{" "}
            {typeof totalCount !== "undefined" ? totalCount : ""}{" "}
            {text[language]?.records || "records"}
          </div>
        </div>
      </div>

      {/* ── Filter inputs grid (same as Attendance) ────────────────── */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
          gap: "20px",
        }}
      >
        {/* Search — uses shared SearchBar component (same as Attendance) */}
        <div>
          <label style={labelStyle}>
            {text[language]?.searchEmployee || "Search Employee"}
          </label>
          <SearchBar
            value={searchTerm}
            onChange={(value) => setSearchTerm(value)}
            placeholder={text[language]?.searchNameIdEmail || "Name, ID, Email..."}
            theme={theme}
            language={language}
            icon={true}
          />
        </div>

        {/* Department — uses ReusableDropdown (same as Attendance StatusDropdown) */}
        <div>
          <label style={labelStyle}>
            <Filter size={16} />
            {text[language]?.department || "Department"}
          </label>
          <div style={{ position: "relative" }}>
            <ReusableDropdown
              items={[
                { key: "", label: text[language]?.allDepartments || "All" },
                ...departmentsList.map((dep) => ({ key: dep, label: dep })),
              ]}
              value={filterDepartment}
              onChange={(val) => setFilterDepartment(val)}
              onSelect={(item) => setFilterDepartment(item.key)}
              onClear={() => setFilterDepartment("")}
              placeholder={text[language]?.searchDepartment || "Search department..."}
              getItemKey={(it) => it.key}
              getItemLabel={(it) => it.label}
              theme={theme}
              language={language}
              maxItems={8}
              showAllOnArrowClick={true}
            />
          </div>
        </div>

        {/* Status — uses ReusableDropdown (same as Attendance StatusDropdown) */}
        <div>
          <label style={labelStyle}>
            <Filter size={16} />
            {text[language]?.Status || "Status"}
          </label>
          <div style={{ position: "relative" }}>
            <ReusableDropdown
              items={[
                { key: "", label: text[language]?.allStatuses || "All" },
                { key: "Active", label: text[language]?.statusActive || "Active" },
                { key: "OnLeave", label: text[language]?.statusOnLeave || "On Leave" },
                { key: "Inactive", label: text[language]?.statusInactive || "Inactive" },
              ]}
              value={filterStatus}
              onChange={(val) => setFilterStatus(val)}
              onSelect={(item) => setFilterStatus(item.key)}
              onClear={() => setFilterStatus("")}
              placeholder={text[language]?.selectStatus || "Select"}
              getItemKey={(it) => it.key}
              getItemLabel={(it) => it.label}
              theme={theme}
              language={language}
              maxItems={6}
              showAllOnArrowClick={true}
            />
          </div>
        </div>

        {/* Company — uses ReusableDropdown (same as Attendance StatusDropdown) */}
        {screenSize !== "xs" && (
          <div>
            <label style={labelStyle}>
              <Filter size={16} />
              {text[language]?.company || "Company"}
            </label>
            <div style={{ position: "relative" }}>
              <ReusableDropdown
                items={[
                  { key: "", label: text[language]?.allCompanies || "All" },
                  ...companiesList.map((c) => ({ key: c, label: c })),
                ]}
                value={filterCompany}
                onChange={(val) => setFilterCompany(val)}
                onSelect={(item) => setFilterCompany(item.key)}
                onClear={() => setFilterCompany("")}
                placeholder={text[language]?.allCompanies || "All"}
                getItemKey={(it) => it.key}
                getItemLabel={(it) => it.label}
                theme={theme}
                language={language}
                maxItems={8}
                showAllOnArrowClick={true}
              />
            </div>
          </div>
        )}
      </div>

      {/* ── Action Buttons ─────────────────────────────────────────────── */}
      {actionButtons && (
        <div style={{ marginTop: "20px" }}>
          {actionButtons}
        </div>
      )}
    </div>
  );
}